import { useMemo } from 'react';
import type { MetricSeries, ScaleGroup, GroupedMetric, ChartPeriod } from '../types';
import { METRIC_LABELS, DIMENSION_COLORS } from '../constants';
import { groupMetricsByScale } from '../utils';

export interface UseSeriesDataOptions {
  hasMetricSelection: boolean;
  selectedDimensions: string[];
  metricSeries: Map<string, Map<string, number>>;
  periods: ChartPeriod[];
  currentTotals: Map<string, number>;
  comparisonTotals: Map<string, number>;
}

export interface SeriesDataResult {
  seriesList: MetricSeries[];
  scaleGroups: ScaleGroup[];
  allGroupedMetrics: GroupedMetric[];
  allValues: number[];
  maxValue: number;
  hasDualAxis: boolean;
  leftAxisGroup: ScaleGroup | undefined;
  rightAxisGroup: ScaleGroup | undefined;
}

/**
 * Transforms metric series data for chart rendering.
 * Handles scale grouping for dual-axis charts.
 */
export function useSeriesData({
  hasMetricSelection,
  selectedDimensions,
  metricSeries,
  periods,
  currentTotals,
  comparisonTotals,
}: UseSeriesDataOptions): SeriesDataResult {
  // Build series list for selected metrics/dimensions
  const seriesList = useMemo((): MetricSeries[] => {
    if (!hasMetricSelection) return [];

    return selectedDimensions.map((metricKey, index) => {
      const values = metricSeries.get(metricKey) || new Map<string, number>();
      const maxValue = Math.max(...Array.from(values.values()), 1);
      return {
        key: metricKey,
        label: METRIC_LABELS[metricKey] || metricKey,
        color: DIMENSION_COLORS[index % DIMENSION_COLORS.length],
        values,
        isCurrency: metricKey === 'total',
        maxValue,
      };
    });
  }, [hasMetricSelection, selectedDimensions, metricSeries]);

  // Smart scale grouping - group metrics by similar magnitude
  const scaleGroups = useMemo((): ScaleGroup[] => {
    if (!hasMetricSelection || seriesList.length === 0) return [];
    return groupMetricsByScale(seriesList);
  }, [hasMetricSelection, seriesList]);

  // Flatten all metrics from scale groups for rendering
  const allGroupedMetrics = useMemo((): GroupedMetric[] => {
    return scaleGroups.flatMap(group =>
      group.metrics.map(m => ({
        ...m,
        axisPosition: group.axisPosition,
        groupMaxValue: group.maxValue,
      }))
    );
  }, [scaleGroups]);

  // Calculate max value for scaling
  const allValues = useMemo((): number[] => {
    if (hasMetricSelection) {
      return periods.flatMap(p =>
        seriesList.map(s => s.values.get(p.key) || 0)
      );
    }
    return periods.flatMap(p => [
      currentTotals.get(p.key) || 0,
      comparisonTotals.get(p.key) || 0,
    ]);
  }, [periods, currentTotals, comparisonTotals, hasMetricSelection, seriesList]);

  const maxValue = Math.max(...allValues, 1);

  // Dual axis detection
  const hasDualAxis = scaleGroups.length === 2;
  const leftAxisGroup = scaleGroups.find(g => g.axisPosition === 'left');
  const rightAxisGroup = scaleGroups.find(g => g.axisPosition === 'right');

  return {
    seriesList,
    scaleGroups,
    allGroupedMetrics,
    allValues,
    maxValue,
    hasDualAxis,
    leftAxisGroup,
    rightAxisGroup,
  };
}
